import React, { useState, useLayoutEffect, useMemo, useContext, useRef } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useNavigate } from "react-router-dom";
import { Spinner } from "reactstrap";
import axios from "axios";
import { _imagesURL, adminBaseURL } from "../../constants/API";
import { NEW_CONTEST } from "../../constants/RoutesUrl";
import { Sponsorpost } from "../../App";
import "./AgendaHeroBanner.css";
const DEFAULT_BANNER_RATIO = 3.5;

function getAdHeight(ad, width) {
  const size = parseFloat(ad?.media_size);
  const ratio = Number.isFinite(size) && size > 0 ? size : DEFAULT_BANNER_RATIO;
  const safeWidth = width > 0 ? width : 945;
  return safeWidth / ratio;
}

function AgendaHeroSlide({ slide, currentHeight, isClickable, onAdClick }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className={`agenda-banner-slide position-relative d-flex align-items-center justify-content-center ${isClickable ? "is-clickable" : ""
        }`}
      style={{
        width: "100%",
        height: currentHeight ? `${Math.round(currentHeight)}px` : "100%",
        cursor: isClickable ? "pointer" : "default",
        background: "#F4F7FB",
        position: "relative",
        overflow: "hidden",
      }}
      onClick={() => onAdClick(slide)}
      aria-label={slide.name}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: loaded ? 0 : 1,
          transition: "opacity 0.4s ease",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <Spinner style={{ color: "#1dc1dd", width: "10px", height: "10px", borderWidth: "1.5px" }} />
      </div>
      {slide.media && (
        <img
          src={slide.media}
          alt={slide.name || "Advertisement"}
          className="agenda-banner-img object-cover"
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = "none";
          }}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
            willChange: "opacity, transform",
          }}
        />
      )}
    </div>
  );
}

export function AgendaHeroBannerShimmer() {
  return (
    <div
      className="agenda-hero-banner agenda-hero-shimmer agenda-shimmer-block"
      aria-hidden="true"
    />
  );
}

export default function AgendaHeroBanner({ ads = [], loading = false }) {
  const navigate = useNavigate();
  const sponsorContext = useContext(Sponsorpost);
  const updateContext = sponsorContext?.updateContext;
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Normalize ads list into standard slide format
  const slides = useMemo(() => {
    const rawList = Array.isArray(ads)
      ? ads
      : ads?.agenda_center && Array.isArray(ads.agenda_center)
        ? ads.agenda_center
        : [];

    if (rawList.length === 0) {
      return [];
    }

    return rawList
      .map((item, idx) => {
        const ad = item?.general_ad || item;
        if (!ad) return null;

        let mediaUrl = ad.media || "";
        if (
          mediaUrl &&
          !mediaUrl.startsWith("http://") &&
          !mediaUrl.startsWith("https://")
        ) {
          mediaUrl = `${_imagesURL}${mediaUrl}`;
        }

        const parsedSize = parseFloat(ad.media_size);
        const mediaRatio =
          Number.isFinite(parsedSize) && parsedSize > 0
            ? parsedSize
            : DEFAULT_BANNER_RATIO;

        if (!mediaUrl) return null;

        return {
          id: ad.id ?? item.id ?? `agenda-ad-${idx}`,
          media: mediaUrl,
          media_size: mediaRatio,
          name: ad.name || "Advertisement",
          rawAd: ad,
          isDefault: false,
        };
      })
      .filter(Boolean);
  }, [ads]);

  // The banner is absent while its shimmer is showing, so this must re-run
  // once the real element mounts after loading. useLayoutEffect applies the
  // measured height before the first visible paint.
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setContainerWidth(Math.round(element.getBoundingClientRect().width));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, [loading, slides.length]);

  // Handle ad interaction (internal, external, or dynamic contest)
  const handleAdClick = (slide) => {
    if (!slide || slide.isDefault) return;
    const ad = slide.rawAd;
    if (!ad) return;

    if (ad.type === "external" && ad.link) {
      const url =
        ad.link.startsWith("http://") || ad.link.startsWith("https://")
          ? ad.link
          : `https://${ad.link}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } else if (ad.type === "internal") {
      switch (ad.internal_model) {
        case "course":
          if (ad.course_id) navigate(`/course-detail/${ad.course_id}`);
          break;
        case "profile":
          if (ad.user_id) navigate(`/profile/${ad.user_id}`);
          break;
        case "clinical_session":
          if (ad.post_id) navigate(`/session-details/${ad.post_id}`);
          break;
        case "webinar":
          if (ad.post_id) navigate(`/webinar-details/${ad.post_id}`);
          break;
        case "clinical_study":
          if (ad.post_id) navigate(`/post/${ad.post_id}`);
          break;
        case "event":
          if (ad.event_id) navigate(`/event-details/${ad.event_id}`);
          break;
        default:
          break;
      }
    } else if (ad.type === "dynamic") {
      navigate(NEW_CONTEST, { state: { dynamicAd: ad } });
    }

    if (typeof updateContext === "function") {
      updateContext(ad);
    }

    // Fire view / click analytics tracking
    try {
      const signupData = JSON.parse(
        localStorage.getItem("SignupData") || "{}"
      );
      const token = signupData?.data?.token;
      if (token && ad.id) {
        axios
          .post(
            `${adminBaseURL}user/ads/view`,
            { device: "Web", ad_id: ad.id, type: "view" },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          .catch(() => { });
      }
    } catch (err) {
      // Non-blocking analytics error
    }
  };

  if (loading) {
    return <AgendaHeroBannerShimmer />;
  }

  if (slides.length === 0) {
    return null;
  }

  const firstSlide = slides[0];
  const currentHeight = containerWidth > 0 ? getAdHeight(firstSlide, containerWidth) : null;
  const bannerRatio = firstSlide.media_size || DEFAULT_BANNER_RATIO;

  return (
    <div
      ref={containerRef}
      className="agenda-hero-banner position-relative"
      style={{
        width: "100%",
        height: currentHeight ? `${Math.round(currentHeight)}px` : undefined,
        aspectRatio: bannerRatio,
        transition: "height 0.3s ease",
        overflow: "hidden",
      }}
    >
      <Carousel
        className="agenda-hero-carousel"
        showArrows={false}
        showStatus={false}
        showThumbs={false}
        showIndicators={false}
        infiniteLoop={slides.length > 1}
        autoPlay={slides.length > 1}
        interval={5000}
        selectedItem={currentSlide}
        onChange={(index) => setCurrentSlide(index)}
        swipeable={slides.length > 1}
        emulateTouch={slides.length > 1}
      >
        {slides.map((slide, index) => {
          const isClickable =
            !slide.isDefault &&
            (Boolean(slide.rawAd?.link) ||
              slide.rawAd?.type === "dynamic" ||
              slide.rawAd?.type === "internal");

          return (
            <AgendaHeroSlide
              key={slide.id || index}
              slide={slide}
              currentHeight={currentHeight}
              isClickable={isClickable}
              onAdClick={handleAdClick}
            />
          );
        })}
      </Carousel>

      {/* Minimal Animated Dot Indicators matching Courses banner */}
      {slides.length > 1 && (
        <div className="cr-banner-dots">
          {slides.map((_, i) => (
            <div
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentSlide(i);
              }}
              className={`cr-banner-dot${currentSlide === i ? " active" : ""}`}
              style={{ width: currentSlide === i ? "24px" : "6px" }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
